import React, { useMemo, useState } from "react";
import { Eye, FolderPlus, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ChevronButton } from "../atoms/ChevronButton";
import { DocumentCountBadge } from "../atoms/DocumentCountBadge";
import { FolderIcon } from "../atoms/FolderIcon";
import { FolderName } from "../atoms/FolderName";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { DocumentRowMeta } from "../atoms/DocumentRowMeta";
import { canPreviewDocumentRow } from "@/utils/previewUtils";
import { useMoveDragOrigin } from "../../hooks/useMoveDragOrigin";
import { movesByFolderKey, proposalTree, reachableFolders } from "../../types/organize";
import type {
  OrganizeProposal,
  ProposalAssignment,
  ProposalDocument,
  ProposalTreeNode,
} from "../../types/organize";

/**
 * The reviewer's drag carries only a document id, on a MIME type of its own.
 *
 * Deliberately NOT the app's `application/doc-id` move payload: that one is a
 * real move into a real folder, guarded by `useFolderDragDrop` against circular
 * moves and unwritable targets. Here the destination is a PROPOSED folder — a
 * string key, often a folder that does not exist yet — and nothing is written
 * until Apply. A distinct type means neither surface can ever accept the
 * other\'s drag by accident.
 */
const ORGANIZE_DRAG_TYPE = "application/organize-doc";

/** Indent per level, matching the folder tree on the Files page. */
const INDENT_PX = 18;
const INDENT_BASE_PX = 8;

export interface ProposalFolderTreeProps {
  proposal: OrganizeProposal | null;
  /** id -> row for every document in the proposal. */
  documents: Map<number, ProposalDocument>;
  /**
   * Selection and renaming. Omitted for the launch-screen preview, which is a
   * picture of a structure the user has not committed to yet — there is nothing
   * to exclude from and no proposal row to rename against.
   */
  excludedIds?: Set<number>;
  onToggleDocument?: (documentId: number, excluded: boolean) => void;
  /** folder key -> the reviewer's replacement name. */
  renames?: Map<string, string>;
  onRename?: (folderKey: string, name: string) => void;
  /** folder key -> why its current name is refused, already localised. */
  renameErrors?: Map<string, string>;
  /**
   * Render the individual files inside each folder. Off for the launch-screen
   * preview, which is about the SHAPE of the result — the per-folder count
   * badge carries the volume, and listing files there would mean fetching every
   * filename again on each change to a picker the user is still adjusting.
   */
  showFiles?: boolean;
  /**
   * Open a file in the app's normal viewer. Without it the eye icon is not
   * rendered at all, which is what the launch-screen preview wants.
   */
  onPreviewDocument?: (document: ProposalDocument) => void;
  /**
   * The reviewer dragged a file onto another proposed folder. Held in dialog
   * state until Apply, like the renames — nothing is written, so a correction
   * costs no round trip and cannot race the proposal's expiry.
   */
  onMoveDocument?: (documentId: number, targetFolderKey: string) => void;
}

interface FolderNodeProps extends Required<Pick<ProposalFolderTreeProps, "documents">> {
  node: ProposalTreeNode;
  movesByKey: Map<string, ProposalAssignment[]>;
  expandedKeys: Set<string>;
  onToggleExpanded: (key: string) => void;
  excludedIds: Set<number>;
  onToggleDocument?: (documentId: number, excluded: boolean) => void;
  renames: Map<string, string>;
  onRename?: (folderKey: string, name: string) => void;
  renameErrors: Map<string, string>;
  editingKey: string | null;
  setEditingKey: (key: string | null) => void;
  showFiles: boolean;
  onPreviewDocument?: (document: ProposalDocument) => void;
  onMoveDocument?: (documentId: number, targetFolderKey: string) => void;
  draggingId: number | null;
  setDraggingId: (id: number | null) => void;
  dragOverKey: string | null;
  setDragOverKey: (key: string | null) => void;
  pressedSelfHandledPart: React.MutableRefObject<boolean>;
}

/**
 * One proposed folder and, when expanded, its subfolders and the files landing
 * in it. Deliberately echoes FolderItem on the Files page — same indent maths,
 * same chevron, same red folder icon — so what the reviewer approves looks like
 * what they will get.
 *
 * Only NEW folders can be renamed. A reused existing folder holds files outside
 * this proposal, so renaming it is a wider action than this screen shows; the
 * apply RPC ignores an override on one regardless.
 */
function FolderNode({
  node,
  movesByKey,
  expandedKeys,
  onToggleExpanded,
  documents,
  excludedIds,
  onToggleDocument,
  renames,
  onRename,
  renameErrors,
  editingKey,
  setEditingKey,
  showFiles,
  onPreviewDocument,
  onMoveDocument,
  draggingId,
  setDraggingId,
  dragOverKey,
  setDragOverKey,
  pressedSelfHandledPart,
}: FolderNodeProps) {
  const { t } = useLanguage();
  const { folder, depth, children } = node;
  const moves = movesByKey.get(folder.key) ?? [];
  const isExpanded = expandedKeys.has(folder.key);
  const hasChildren = children.length > 0 || (showFiles && moves.length > 0);
  const isNew = folder.existing_folder_id === null;
  const isEditing = editingKey === folder.key;
  const renamed = renames.get(folder.key);
  const displayName = renamed ?? folder.name;
  const error = renameErrors.get(folder.key);
  const canRename = isNew && !!onRename;
  const selectable = !!onToggleDocument && moves.length > 0;
  // A drag is in flight and this folder can take it. The file's CURRENT folder
  // is excluded: dropping a file where it already is would read as an action
  // and do nothing.
  const isDropTarget =
    !!onMoveDocument && draggingId !== null && !moves.some((m) => m.document_id === draggingId);
  const isDragOver = isDropTarget && dragOverKey === folder.key;

  const excludedInFolder = moves.filter((m) => excludedIds.has(m.document_id)).length;
  const folderChecked =
    excludedInFolder === 0
      ? true
      : excludedInFolder === moves.length
        ? false
        : ("indeterminate" as const);

  const toggleExpanded = () => {
    if (hasChildren && !isEditing) onToggleExpanded(folder.key);
  };

  return (
    <div>
      <div
        // Matches FolderItem: a row is a real button, so it is reachable and
        // operable from the keyboard rather than being mouse-only.
        role="button"
        tabIndex={hasChildren ? 0 : -1}
        aria-expanded={hasChildren ? isExpanded : undefined}
        className={cn(
          "group flex items-center rounded-md py-1.5 transition-all duration-200 ease-out",
          "select-none hover:bg-slate-50 hover:shadow-folder-hover",
          hasChildren && "cursor-pointer",
          error && "bg-destructive/5 ring-1 ring-inset ring-destructive/30",
          // The same two-tier highlight the real folder rows use: every folder
          // is a possible destination while a drag is in flight, and the one
          // under the pointer is the one that will take it.
          isDropTarget && "border-blue-200 bg-blue-50/40 ring-1 ring-inset ring-blue-200",
          isDragOver && "relative z-10 bg-blue-100 ring-2 ring-inset ring-blue-500"
        )}
        style={{ paddingLeft: depth * INDENT_PX + INDENT_BASE_PX }}
        data-drop-available={isDropTarget || undefined}
        onDragOver={
          isDropTarget
            ? (e) => {
                // preventDefault is what makes this a valid drop target at all.
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dragOverKey !== folder.key) setDragOverKey(folder.key);
              }
            : undefined
        }
        onDragLeave={
          isDropTarget
            ? (e) => {
                // Only when the pointer has genuinely left this row — moving
                // onto a child fires dragleave here too, and clearing then
                // makes the highlight flicker.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setDragOverKey(null);
                }
              }
            : undefined
        }
        onDrop={
          isDropTarget
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                const raw = e.dataTransfer.getData(ORGANIZE_DRAG_TYPE);
                setDragOverKey(null);
                setDraggingId(null);
                const id = Number(raw);
                if (raw && Number.isFinite(id)) onMoveDocument?.(id, folder.key);
              }
            : undefined
        }
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (isEditing) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5 pr-2">
          {selectable ? (
            <Checkbox
              checked={folderChecked}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(checked) => {
                for (const move of moves) onToggleDocument?.(move.document_id, checked !== true);
              }}
              aria-label={displayName}
            />
          ) : (
            // Keeps names aligned with the folders that do carry a checkbox.
            <div className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <ChevronButton
            isExpanded={isExpanded}
            hasChildren={hasChildren}
            onClick={toggleExpanded}
          />
          {isNew ? (
            <FolderPlus className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          ) : (
            <FolderIcon isOpen={isExpanded} className="shrink-0" />
          )}
          <div className="min-w-0 flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <FolderName
              name={displayName}
              isEditing={isEditing}
              onChange={(value) => onRename?.(folder.key, value)}
              onConfirm={(value) => {
                onRename?.(folder.key, value);
                setEditingKey(null);
              }}
              onCancel={() => {
                // Escape restores the proposal's own name.
                onRename?.(folder.key, folder.name);
                setEditingKey(null);
              }}
            />
          </div>
          {/* Only new folders are badged: the red vs emerald icon already says
              which is which, so an "existing" chip on every other row is noise. */}
          {isNew && !isEditing && (
            <Badge
              variant="outline"
              className="shrink-0 border-emerald-200 bg-emerald-50 font-normal text-emerald-700"
            >
              {renamed && renamed !== folder.name
                ? t.documents.organize.renamedBadge
                : t.documents.organize.newFolderBadge}
            </Badge>
          )}
        </div>

        {/* Right-hand group, like FolderItem's: count plus hover-revealed actions. */}
        <div className="flex shrink-0 items-center gap-2 pr-2" onClick={(e) => e.stopPropagation()}>
          {!isEditing && <DocumentCountBadge count={moves.length} />}
          {canRename && !isEditing && (
            <button
              type="button"
              className="rounded p-1 text-slate-400 opacity-0 transition-opacity duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
              aria-label={t.documents.organize.renameFolder}
              title={t.documents.organize.renameFolder}
              onClick={() => setEditingKey(folder.key)}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <p
          className="pb-1 text-xs text-destructive"
          style={{ paddingLeft: depth * INDENT_PX + INDENT_BASE_PX + 24 }}
        >
          {error}
        </p>
      )}

      {isExpanded && (
        <div className="animate-folder-expand space-y-0.5">
          {children.map((child) => (
            <FolderNode
              key={child.folder.key}
              node={child}
              movesByKey={movesByKey}
              expandedKeys={expandedKeys}
              onToggleExpanded={onToggleExpanded}
              documents={documents}
              excludedIds={excludedIds}
              onToggleDocument={onToggleDocument}
              renames={renames}
              onRename={onRename}
              renameErrors={renameErrors}
              editingKey={editingKey}
              setEditingKey={setEditingKey}
              showFiles={showFiles}
              onPreviewDocument={onPreviewDocument}
              onMoveDocument={onMoveDocument}
              draggingId={draggingId}
              setDraggingId={setDraggingId}
              dragOverKey={dragOverKey}
              setDragOverKey={setDragOverKey}
              pressedSelfHandledPart={pressedSelfHandledPart}
            />
          ))}
          {showFiles &&
            moves.map((move) => {
              const excluded = excludedIds.has(move.document_id);
              const doc = documents.get(move.document_id);
              const filename = doc?.filename ?? `#${move.document_id}`;
              const canPreview =
                !!doc &&
                !!onPreviewDocument &&
                canPreviewDocumentRow({
                  filename: doc.filename,
                  content_type: doc.content_type,
                  state: doc.state,
                });
              const draggable = !!onMoveDocument && !excluded;
              return (
                <div
                  key={move.document_id}
                  className={cn(
                    // The Documents page's own row vocabulary — icon, name, and
                    // a size/when meta line under it — so what the reviewer
                    // approves reads like the list they approve it into.
                    "group/file flex items-start gap-2 rounded-md py-1.5 pr-2 transition-colors",
                    "hover:bg-[#f7f7f7]",
                    draggable && "cursor-grab active:cursor-grabbing",
                    draggingId === move.document_id && "opacity-45",
                    excluded && "opacity-55"
                  )}
                  style={{ paddingLeft: (depth + 1) * INDENT_PX + INDENT_BASE_PX }}
                  // The reason lives here rather than beside the filename: it
                  // explains a decision the reviewer only questions occasionally,
                  // and inline it competes with the name for the same glance.
                  title={move.reason || undefined}
                  draggable={draggable}
                  onDragStart={(e) => {
                    // A press that landed on the checkbox or the eye owns
                    // itself; dragstart fires at this row either way, so the
                    // capture-phase latch is the only thing that still knows.
                    if (pressedSelfHandledPart.current) {
                      e.preventDefault();
                      return;
                    }
                    e.dataTransfer.setData(ORGANIZE_DRAG_TYPE, String(move.document_id));
                    e.dataTransfer.effectAllowed = "move";
                    setDraggingId(move.document_id);
                  }}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setDragOverKey(null);
                  }}
                >
                  {onToggleDocument && (
                    <Checkbox
                      className="mt-1 shrink-0"
                      checked={!excluded}
                      onCheckedChange={(checked) =>
                        onToggleDocument(move.document_id, checked !== true)
                      }
                      aria-label={filename}
                    />
                  )}
                  <FileTypeIcon filename={filename} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    {/*
                      The eye sits immediately after the name, not out at the
                      row's edge: it acts on THIS file, and parked in a trailing
                      column it reads as a row-level action and is a long way
                      from the thing it opens.
                    */}
                    <div className="flex min-w-0 items-center gap-1">
                      <span
                        className={cn(
                          "min-w-0 truncate text-sm font-medium text-slate-700",
                          excluded && "line-through"
                        )}
                      >
                        {filename}
                      </span>
                      {canPreview && (
                        <button
                          type="button"
                          className="shrink-0 rounded p-0.5 text-slate-600 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-900 focus-visible:opacity-100 group-hover/file:opacity-100"
                          aria-label={t.documents.organize.previewFile}
                          title={t.documents.organize.previewFile}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPreviewDocument?.(doc);
                          }}
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                    {doc && (
                      <DocumentRowMeta
                        size={doc.size}
                        createdAt={doc.created_at}
                        showUser={false}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

/**
 * The proposed structure drawn the way the Files page draws the real one.
 * Shared by the review screen (selectable, renameable) and the tag-mode launch
 * preview (read-only) so the two can never disagree about what will happen.
 */
export function ProposalFolderTree({
  proposal,
  documents,
  excludedIds,
  onToggleDocument,
  renames,
  onRename,
  renameErrors,
  showFiles = true,
  onPreviewDocument,
  onMoveDocument,
}: ProposalFolderTreeProps) {
  const { t } = useLanguage();
  const tree = useMemo(() => proposalTree(proposal), [proposal]);
  const movesByKey = useMemo(() => movesByFolderKey(proposal), [proposal]);
  const folders = useMemo(() => (proposal ? reachableFolders(proposal) : []), [proposal]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [keepDragOver, setKeepDragOver] = useState(false);
  const pressedSelfHandledPart = useMoveDragOrigin();

  // Everything open on arrival: the proposal IS the thing being reviewed, so
  // hiding it behind chevrons would make the reviewer click to see the plan.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const expandedKeys = useMemo(
    () => new Set(folders.map(({ folder }) => folder.key).filter((k) => !collapsedKeys.has(k))),
    [folders, collapsedKeys]
  );
  const toggleExpanded = (key: string) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      {tree.map((node) => (
        <FolderNode
          key={node.folder.key}
          node={node}
          movesByKey={movesByKey}
          expandedKeys={expandedKeys}
          onToggleExpanded={toggleExpanded}
          documents={documents}
          excludedIds={excludedIds ?? new Set()}
          onToggleDocument={onToggleDocument}
          renames={renames ?? new Map()}
          onRename={onRename}
          renameErrors={renameErrors ?? new Map()}
          editingKey={editingKey}
          setEditingKey={setEditingKey}
          showFiles={showFiles}
          onPreviewDocument={onPreviewDocument}
          onMoveDocument={onMoveDocument}
          draggingId={draggingId}
          setDraggingId={setDraggingId}
          dragOverKey={dragOverKey}
          setDragOverKey={setDragOverKey}
          pressedSelfHandledPart={pressedSelfHandledPart}
        />
      ))}

      {/*
        Dragging a file OUT of the structure. This is not a third kind of
        override on the server — it is the exclusion the checkboxes already
        write, reached by dragging instead of unticking, so a reviewer who is
        rearranging by hand never has to switch input styles mid-task. It only
        appears while a drag is in flight; a permanent empty well would read as
        a folder.
      */}
      {onToggleDocument && draggingId !== null && (
        <div
          className={cn(
            "mt-1 rounded-md border border-dashed px-3 py-2 text-xs transition-colors",
            keepDragOver
              ? "border-blue-500 bg-blue-100 text-blue-800"
              : "border-slate-300 bg-slate-50/60 text-muted-foreground"
          )}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setKeepDragOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setKeepDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            const raw = e.dataTransfer.getData(ORGANIZE_DRAG_TYPE);
            setKeepDragOver(false);
            setDraggingId(null);
            setDragOverKey(null);
            const id = Number(raw);
            if (raw && Number.isFinite(id)) onToggleDocument(id, true);
          }}
        >
          {t.documents.organize.dropToKeep}
        </div>
      )}
    </>
  );
}
